import React, { useState } from "react";
import { Typography, Avatar, Box, InputBase } from "@mui/material";
import Loading from "../../components/Loading";
import { PlusIcon, Star } from "lucide-react";
import { api } from "../../api/api";
import { follow_url, seller_url } from "../../api/urls";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { useFetchWithPagination } from "../../hooks/useFetchWithPagination";

// ----- Search components -----
const Search = ({ children, sx }) => (
  <Box
    sx={{
      position: "relative",
      borderRadius: 1,
      backgroundColor: "#f1f1f1",
      width: "auto",
      maxWidth: 300,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SearchIconWrapper = () => (
  <Box
    sx={{
      p: 1,
      height: "100%",
      position: "absolute",
      right: 0,
      top: 0,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "gray",
    }}
  >
    <SearchIcon sx={{ color: "#000" }} />
  </Box>
);

const StyledInputBase = (props) => (
  <InputBase sx={{ color: "black", pl: 2, pr: 5, width: "100%" }} {...props} />
);

// ----- Sellers Page -----
const SellersPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  // const [showAll, setShowAll] = useState(false);

  const token = useSelector((state) => state.auth.accessToken);

  // Fetch sellers
  const {
    data,
    loading: loadingSellers,
    pagination,
  } = useFetchWithPagination(seller_url);
  // console.log("SELLERS API RESPONSE:", sellers);
  const sellers = data?.results;
  if (loadingSellers) return <Loading />;

  const filteredSellers = sellers?.filter((seller) =>
    (seller.name || seller.company_name)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  // const visibleSellers = showAll
  //   ? filteredSellers
  //   : filteredSellers?.slice(0, 4);

  return (
    <div className="bg-[#F8FAFC] py-4">
      <div className="max-w-[1500px] mx-auto relative">
        <div className="flex items-center p-5 bg-white rounded-lg mb-4 gap-2 sticky top-0 left-0">
          <p className="md:text-lg">Sellers</p>
          <Search sx={{ ml: "auto" }}>
            <SearchIconWrapper />
            <StyledInputBase
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Search>
        </div>

        {/* <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          {filteredSellers?.length > 4 && (
            <Typography
              variant="h6"
              sx={{ cursor: "pointer", color: "#8c8c8c", fontSize: "18px" }}
              onClick={() => setShowAll((p) => !p)}
            >
              {showAll ? "Show Less" : "See All"}
            </Typography>
          )}
        </Box> */}

        <div className="grid md:grid-cols-2 gap-5">
          {filteredSellers?.map((seller) => (
            <SellerCard key={seller.id} seller={seller} token={token} />
          ))}
        </div>
        {pagination}
      </div>
    </div>
  );
};

// ----- Individual Seller Card -----
const SellerCard = ({ seller, token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [follow, setFollow] = useState(seller?.follower);
  const [count, setCount] = useState(seller?.follow_counts);
  const isFollowing = seller.follower;

  const handleFollow = async () => {
    setLoading(true);
    try {
      const url = follow_url(seller.id);
      await api.patch(url, { likes: true });
      setCount((prev) => {
        if (follow) {
          return prev - 1;
        } else {
          return prev + 1;
        }
      });
      setFollow((prev) => !prev);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${isFollowing ? "unfollow" : "follow"} seller.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-5">
      <div className="flex md:items-center justify-between md:flex-row flex-col">
        <div className="flex md:items-center gap-3 flex-1 md:flex-row flex-col">
          <Avatar
            src={seller.company_image}
            alt={seller.name}
            sx={{
              width: 110,
              height: 110,
              mx: "auto",
              border: "2px solid #f5f5f5",
            }}
          >
            {(!seller.company_image &&
              (seller.name?.[0] || seller.company_name?.[0])) ||
              ""}
          </Avatar>
          <div className="flex items-center md:justify-between flex-1 gap-3 md:flex-row flex-col">
            <p
              className="my-2 md:text-2xl hover:underline underline-offset-5 cursor-pointer"
              onClick={() =>
                navigate(`/sellerProfile/${seller.id}`, {
                  state: { seller: { ...seller, follower: seller.follower } },
                })
              }
            >
              {seller.name || seller.company_name}
            </p>
            <div>
              <button
                onClick={handleFollow}
                disabled={loading}
                className={`border ${follow ? "bg-white hover:bg-black hover:text-white" : "bg-black text-white hover:bg-white hover:text-black"} transition-all py-1 px-4 rounded-md`}
              >
                {loading ? "loading..." : follow ? "Unfollow" : "Follow"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-2">
        <StatBox label="Products" value={seller?.product_list?.length || 0} />
        <StatBox label="Following" value={count} />
        <StatBox
          label="Ratings"
          value={
            <Typography
              className="flex items-center gap-2 justify-center"
              sx={{ fontSize: 20, fontWeight: 500 }}
            >
              {seller?.rating || 0}{" "}
              <Star size={16} color="#f4c430" fill="#f4c430" />
            </Typography>
          }
        />
      </div>
      {seller.description && (
        <Typography
          variant="body2"
          sx={{ my: 2, maxWidth: 800, color: "#000" }}
        >
          {seller.description}
        </Typography>
      )}

      {seller?.brand_list?.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          mt={4}
          mb={4}
          justifyContent="flex-start"
        >
          {seller?.brand_list?.slice(0, 3).map((brand) => (
            <Box
              key={brand.id}
              component="img"
              src={brand.img}
              alt={brand.name}
              title={brand.name}
              onClick={() =>
                navigate(`/sellerProfile/${seller.id}`, {
                  state: {
                    seller: {
                      ...seller,
                      follower: seller.follower,
                    },
                  },
                })
              }
              className="border"
              sx={{
                width: { xs: "48%", sm: "30%", md: "22%" },
                aspectRatio: "1 / 1",
                maxWidth: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
                borderRadius: 2,
                cursor: "pointer",
                overflow: "hidden",
                transition: "transform 0.3s",
                "&:hover": { transform: "scale(1.05)" },
                objectFit: "cover",
              }}
            />
          ))}
          {seller?.brand_list?.length > 3 && (
            <Box
              className="border flex justify-center items-center bg-slate-100 rounded-md text-slate-400 cursor-pointer"
              sx={{
                width: { xs: "48%", sm: "30%", md: "22%" },
                aspectRatio: "1 / 1",
              }}
              onClick={() =>
                navigate(`/sellerProfile/${seller.id}`, {
                  state: { seller: { ...seller, follower: seller.follower } },
                })
              }
            >
              <p className="flex text-3xl items-center ">
                <PlusIcon /> {seller?.brand_list?.length - 3}{" "}
              </p>
            </Box>
          )}
        </Box>
      )}
    </div>
    // {/* </motion.div> */}
  );
};

// ----- Stat Box -----
const StatBox = ({ label, value }) => (
  <Box textAlign="center" minWidth={80}>
    <h4 className="text-lg text-slate-500">{label}</h4>
    <Typography variant="body1" sx={{ fontSize: 20 }} fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

export default SellersPage;
